import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Actions, ofActionCompleted, ofActionDispatched, Select, Store } from '@ngxs/store';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { FieldDef } from 'app/core/models';
import { LayoutService } from 'app/layout/layout.service';
import { formatFilterItem, getDrillFilters } from 'app/modules/reporting/reporting.functions';
import { DrillFilter, ReportLayoutChart, ReportLayoutDefinition } from 'app/modules/reporting/reporting.types';
import { BuildChartData } from 'app/modules/reporting/state/actions';
import { ReportingState } from 'app/modules/reporting/state/state';
import { ReportLayout, ReportType } from 'app/modules/reports/reports.types';
import { GraphqlFilter } from 'app/shared/models/graphql';
import { noop, Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { DrillDataVisualizationComponent } from './drill-data/drill-data.component';
import { ChartType } from 'app/shared/components/chart/chart.types';
import { NewPermission } from '@core/enums';

@Component({
  selector: 'app-reporting-builder-visualizations',
  templateUrl: './visualizations.component.html',
  styleUrls: ['./visualizations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VisualizationsComponent extends OnDestroyMixin implements OnInit {
  @Input() options: any;
  @Input() fields: FieldDef[];
  @Input() layout: ReportLayout<ReportLayoutDefinition>;
  @Input() columns: string[];
  @Input() isTimeSeries = false;
  @Input() allowRelatedObjectFields = false;

  @Output() removeChart = new EventEmitter<Partial<ReportLayoutChart>>();
  @Output() refreshChart = new EventEmitter();

  readonly stateChartData$: Observable<ReportLayoutChart[]> = this.store.select(ReportingState.getChartData);

  chartData$: Observable<ReportLayoutChart[]>;
  ui = { isLoading: false };
  protected readonly NewPermission = NewPermission;

  private drillFilters: DrillFilter = {};
  private stateChartData: ReportLayoutChart;

  private get hasVisualization(): boolean {
    return this.layout.definition.visualizations?.length > 0;
  }

  private get hasVisualizationGroupBySet(): boolean {
    return this.hasVisualization ? !!this.layout.definition.visualizations[0].series_value : false;
  }

  constructor(
    private readonly matDialog: MatDialog,
    private readonly layoutService: LayoutService,
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.chartData$ = this.stateChartData$.pipe(
      untilComponentDestroyed(this),
      filter((data) => !!data),
      tap(([chartData]) => {
        if (chartData) {
          this.stateChartData = chartData;
          this.updateDrillFilters();
        }
      }),
    );

    this.actions$
      .pipe(
        ofActionDispatched(BuildChartData.Action),
        tap(() => {
          this.ui = { ...this.ui, isLoading: true };
          this.changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        ofActionCompleted(BuildChartData.Action),
        tap(() => {
          this.ui = { ...this.ui, isLoading: false };
          this.changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);
  }

  create(): void {
    this.layoutService.emitCommon('openCreateVisualization', true, 'subject');
  }

  edit(): void {
    this.layoutService.emitCommon('openEditVisualization', true, 'subject');
  }

  onDataPointSelection(event: { seriesIndex: number; dataPointIndex: number }): void {
    if (this.layout.report_type === ReportType.TimeSeries) {
      return;
    }

    this.updateDrillFilters();

    const innerFilters = [this.drillFilters[event.dataPointIndex]];
    const layoutFilter = JSON.parse((this.layout.filters as unknown as GraphqlFilter).fjson) as any[];
    const category = this.stateChartData.rankedCategories[event.dataPointIndex];
    const seriesValue = this.stateChartData.series_value;

    const fields = this.getFields();

    let series;
    if (seriesValue) {
      const field = this.fields.find(({ name }) => name === seriesValue);
      let fieldType = field?.type;
      let isRecord = fieldType === 'record';
      series = this.stateChartData.series[event.seriesIndex][seriesValue.endsWith('id') || isRecord ? 'value' : 'name'];

      if (field.meta.shapeShifter) {
        const fieldName = field.meta.shapeShifter.name;
        fieldType = field.meta.shapeShifter.type;
        isRecord = fieldType === 'record';
        series = this.stateChartData.series[event.seriesIndex][fieldName.endsWith('id') || isRecord ? 'value' : 'name'];
      }

      innerFilters.push(formatFilterItem(seriesValue, series, this.stateChartData.series[event.seriesIndex], fields));
    }

    this.matDialog
      .open(DrillDataVisualizationComponent, {
        autoFocus: false,
        width: '100%',
        data: {
          relatedToType: this.layout.definition.related_to_type,
          category,
          series,
          columns: this.layout.definition.columns,
          filters: {
            fjson: JSON.stringify([...layoutFilter, ...innerFilters]),
          },
          fields,
          options: this.options,
          fieldDefs: this.getFields(),
        },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe(noop);
  }

  private getFields(): FieldDef[] {
    let fields = [...this.fields];

    if (this.allowRelatedObjectFields) {
      fields = [...fields];
    }

    return fields;
  }

  private updateDrillFilters(): void {
    if (this.layout.report_type !== ReportType.TimeSeries) {
      if (this.stateChartData.chart_type !== ChartType.Line) {
        this.drillFilters = getDrillFilters(this.stateChartData, this.getFields(), this.hasVisualizationGroupBySet);
      }
    }
  }
}
